import { ConfigApi, IdentityApi } from '@backstage/core-plugin-api';
import {
  GetEntitiesResponse,
  GetEntityRefsResponse,
  GetFilterValuesResponse,
  ListFiltersResponse,
  PdpFilterApi,
} from './pdp-filter-api';
import { Entity } from '@backstage/catalog-model';
import pLimit from 'p-limit';
import { fetchWithTracingAndRetries } from '@internal/plugin-pdp-core-api';
import { PdpLoggerApi } from '@internal/plugin-pdp-logger-frontend';

export class PdpFilterClient implements PdpFilterApi {
  private readonly configApi: ConfigApi;
  private readonly identityApi: IdentityApi;
  private readonly pdpLoggerApi: PdpLoggerApi;

  constructor(options: {
    identityApi: IdentityApi;
    configApi: ConfigApi;
    pdpLoggerApi: PdpLoggerApi;
  }) {
    this.identityApi = options.identityApi;
    this.configApi = options.configApi;
    this.pdpLoggerApi = options.pdpLoggerApi;
  }

  async listFilters(): Promise<ListFiltersResponse> {
    const { token } = await this.identityApi.getCredentials();

    const backendHost = this.configApi.getString('backend.baseUrl');

    const endpoint = `${backendHost}/api/pdp-filter/beta/filters`;
    const response = await fetchWithTracingAndRetries(
      endpoint,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'Content-Type': 'application/json',
        },
      },
      this.pdpLoggerApi,
    );

    const data: ListFiltersResponse = await response.json();

    return data;
  }

  async getFilterValues(filterId: string): Promise<GetFilterValuesResponse> {
    const { token } = await this.identityApi.getCredentials();

    const backendHost = this.configApi.getString('backend.baseUrl');

    const endpoint = `${backendHost}/api/pdp-filter/beta/filters/${filterId}`;
    const response = await fetchWithTracingAndRetries(
      endpoint,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'Content-Type': 'application/json',
        },
      },
      this.pdpLoggerApi,
    );

    const data: GetFilterValuesResponse = await response.json();

    return data;
  }

  async getEntities(
    filters: Record<string, string[]>,
    includeComponentsForBU?: boolean,
    volumeTesting?: boolean,
  ): Promise<GetEntitiesResponse> {
    const { token } = await this.identityApi.getCredentials();

    const backendHost = this.configApi.getString('backend.baseUrl');

    const normalizedFilters = Object.fromEntries(
      Object.entries(filters).map(([key, value]) => [key.toLowerCase(), value]),
    );

    const filterNames = Object.keys(normalizedFilters);

    const endpoint = `${backendHost}/api/pdp-filter/beta/entities`;

    // Check if filters has departments filter and it contains more than 50 departments - remove departments from filters - batch into batches of 50 - make parallel calls for batches and the remaining filters
    if (
      filterNames.includes('departments') &&
      normalizedFilters.departments.length > 5
    ) {
      const batches = [];
      for (let i = 0; i < filters.departments.length; i += 5) {
        batches.push(filters.departments.slice(i, i + 5));
      }

      const limit = pLimit(10);

      const responses = await Promise.all(
        batches.map(batch =>
          limit(async () => {
            const response = await fetchWithTracingAndRetries(
              endpoint,
              {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${token}`,
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  filters: { departments: batch },
                  includeComponentsForBU,
                }),
              },
              this.pdpLoggerApi,
            );

            if (response.ok) {
              // Await and return the parsed JSON response
              return response.json();
            }

            // If all retries fail, return undefined (should not happen)
            return undefined;
          }),
        ),
      );

      // gather the entities from all responses removing duplicates
      const entities: Entity[] = [];
      const entityIds = new Set<string>();
      let total = 0;
      let failedChunks = 0;

      for (const response of responses) {
        if (!response || !response.entities) {
          failedChunks++;
          continue; // Skip if response is undefined or does not contain entities
        }

        for (const entity of response.entities) {
          if (!entityIds.has(entity.metadata.uid)) {
            entities.push(entity);
            entityIds.add(entity.metadata.uid);
          }
        }

        total += response._meta.total;
      }

      if (
        (filterNames.includes('owners') &&
          normalizedFilters.owners.length > 0) ||
        (filterNames.includes('businessunits') &&
          normalizedFilters.businessunits.length > 0)
      ) {
        // remove departments from normalized filters and call endpoint with the remaining filters
        const remainingFilters = { ...normalizedFilters };
        delete remainingFilters.departments;

        const remainingFiltersResponse = await fetchWithTracingAndRetries(
          endpoint,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              // eslint-disable-next-line @typescript-eslint/naming-convention
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              filters: remainingFilters,
              includeComponentsForBU,
            }),
          },
          this.pdpLoggerApi,
        );

        const remainingFiltersData: GetEntitiesResponse =
          await remainingFiltersResponse.json();

        // intersect the owners with the existing entities
        const remainingFiltersEntities = remainingFiltersData.entities.filter(
          entity =>
            entities.some(
              existingEntity =>
                existingEntity.metadata.uid === entity.metadata.uid,
            ),
        );

        // set the entities to the intersection of the owners and the existing entities
        entities.length = 0; // Clear the existing entities
        entities.push(...remainingFiltersEntities);
        total = remainingFiltersEntities.length; // Update total to the length of the owner entities
      }

      return {
        entities,
        _meta: {
          total,
          failedChunks,
        },
      };
    }

    const response = await fetchWithTracingAndRetries(
      endpoint,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          filters,
          includeComponentsForBU,
          volumeTesting,
        }),
      },
      this.pdpLoggerApi,
    );

    const data: GetEntitiesResponse = await response.json();

    return data;
  }

  async isHrLeader(userId: string): Promise<boolean> {
    const { token } = await this.identityApi.getCredentials();

    const backendHost = this.configApi.getString('backend.baseUrl');

    const endpoint = `${backendHost}/api/pdp-filter/beta/is-hr-leader`;
    const response = await fetchWithTracingAndRetries(
      endpoint,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId }),
      },
      this.pdpLoggerApi,
    );

    const data = await response.json();

    return data.isHrLeader;
  }

  async getEntityRefs(
    filters: Record<string, string[]>,
    includeComponentsForBU: boolean,
    _userOwnershipRefs?: string[],
    volumeTesting?: boolean,
  ): Promise<GetEntityRefsResponse> {
    const { token } = await this.identityApi.getCredentials();

    const backendHost = this.configApi.getString('backend.baseUrl');

    const endpoint = `${backendHost}/api/pdp-filter/beta/entity-refs`;

    const normalizedFilters = Object.fromEntries(
      Object.entries(filters).map(([key, value]) => [key.toLowerCase(), value]),
    );

    const filterNames = Object.keys(normalizedFilters);

    // Handle large department lists by batching (same pattern as getEntities)
    if (
      filterNames.includes('departments') &&
      normalizedFilters.departments.length > 5
    ) {
      const batches: string[][] = [];
      for (let i = 0; i < normalizedFilters.departments.length; i += 5) {
        batches.push(normalizedFilters.departments.slice(i, i + 5));
      }

      const limit = pLimit(10);

      const responses = await Promise.all(
        batches.map(batch =>
          limit(async () => {
            const response = await fetchWithTracingAndRetries(
              endpoint,
              {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  filters: { departments: batch },
                  includeComponentsForBU,
                }),
              },
              this.pdpLoggerApi,
            );

            if (response.ok) {
              return response.json() as Promise<GetEntityRefsResponse>;
            }
            return undefined;
          }),
        ),
      );

      // Merge entity refs from all batches, deduplicating
      const entityRefSet = new Set<string>();
      const maskedRefSet = new Set<string>();
      let failedChunks = 0;

      for (const response of responses) {
        if (!response) {
          failedChunks++;
          continue;
        }
        for (const ref of response.entityRefs) {
          entityRefSet.add(ref);
        }
        for (const ref of response.maskedEntityRefs) {
          maskedRefSet.add(ref);
        }
      }

      let entityRefs = [...entityRefSet];
      let maskedEntityRefs = [...maskedRefSet];

      // If there are other filters, intersect with those results
      if (
        (filterNames.includes('owners') &&
          normalizedFilters.owners.length > 0) ||
        (filterNames.includes('businessunits') &&
          normalizedFilters.businessunits.length > 0)
      ) {
        const remainingFilters = { ...normalizedFilters };
        delete remainingFilters.departments;

        const remainingResponse = await fetchWithTracingAndRetries(
          endpoint,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              filters: remainingFilters,
              includeComponentsForBU,
            }),
          },
          this.pdpLoggerApi,
        );

        if (!remainingResponse.ok) {
          // If intersection request fails, return the batched results as-is
          return {
            entityRefs,
            maskedEntityRefs,
            _meta: {
              total: entityRefs.length,
              failedChunks,
            },
          };
        }

        const remainingData: GetEntityRefsResponse =
          await remainingResponse.json();

        // Intersect
        const remainingSet = new Set(remainingData.entityRefs);
        entityRefs = entityRefs.filter(ref => remainingSet.has(ref));
        maskedEntityRefs = maskedEntityRefs.filter(ref =>
          remainingSet.has(ref),
        );
      }

      return {
        entityRefs,
        maskedEntityRefs,
        _meta: {
          total: entityRefs.length,
          failedChunks,
        },
      };
    }

    // Standard single request
    const response = await fetchWithTracingAndRetries(
      endpoint,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          filters,
          includeComponentsForBU,
          volumeTesting,
        }),
      },
      this.pdpLoggerApi,
    );

    const data: GetEntityRefsResponse = await response.json();

    return data;
  }
}
